import * as React from 'react'

import { RotateCwIcon } from 'lucide-react'
import { Map as MapboxMap } from 'mapbox-gl'
import { observer } from 'mobx-react'

import { NO_FILL } from './MarkupDrawController'
import { RESIZE_HANDLE_CLASS, ROTATE_HANDLE_CLASS } from './markupHandleStyle'
import { ITextBox } from './MarkupTextStore'
import {
  specOfBox,
  TEXT_PADDING_PX,
  textMarkupBoxStyle,
} from './markupTextStyle'
import { MOVE_SLOP_PX } from './markupTextTap'

interface IProps {
  box: ITextBox
  screen: { x: number; y: number }
  scale: number
  // Both 0 at the usual north-up/top-down editing pose.
  pitch: number
  bearing: number
  isSelected: boolean
  isEditing: boolean
  // Select tool active: the box catches pointer events. While a drawing tool is
  // active it's inert so the tool's click draws through it instead of selecting.
  interactive: boolean
  map: MapboxMap
  // Every tap that is not a drag; the store pairs taps into a double-tap.
  onTap: (x: number, y: number, t: number) => void
  onTapCancel: () => void
  onTextChange: (text: string) => void
  onBlur: (e: React.FocusEvent<HTMLDivElement>) => void
  onMove: (lng: number, lat: number) => void
  onResize: (widthPx: number) => void
  onRotate: (deg: number) => void
}

const HANDLE = `tw:absolute ${RESIZE_HANDLE_CLASS}`

/**
 * One geo-anchored text box. Position comes from the parent (projected anchor);
 * font/box size are fixed pixels. Uncontrolled contentEditable (content set once
 * on mount, read on input) so the caret never jumps. Drag the body to move
 * (unproject via the map), drag the bottom-right handle to fix the width.
 */
const MarkupTextBox: React.FC<IProps> = ({
  box,
  screen,
  scale,
  pitch,
  bearing,
  isSelected,
  isEditing,
  interactive,
  map,
  onTap,
  onTapCancel,
  onTextChange,
  onBlur,
  onMove,
  onResize,
  onRotate,
}) => {
  const editableRef = React.useRef<HTMLDivElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const movedRef = React.useRef(false)
  const downRef = React.useRef<{ x: number; y: number } | null>(null)

  const markMovedIfDragged = (x: number, y: number): void => {
    const down = downRef.current
    if (down && Math.hypot(x - down.x, y - down.y) > MOVE_SLOP_PX) {
      movedRef.current = true
    }
  }

  // Re-sync while NOT editing to avoid caret jumps; during editing the DOM is the source of truth.
  // A layout effect, so a box mounting with text shows it in its first painted frame.
  React.useLayoutEffect(() => {
    const el = editableRef.current
    if (!el || isEditing) return
    if (el.innerText !== box.text) el.innerText = box.text
  }, [box.text, isEditing])

  // Layout effect (not passive): runs during the placing tap's synchronous commit,
  // so focus() lands inside the gesture — required for iOS to raise the keyboard.
  React.useLayoutEffect(() => {
    const el = editableRef.current
    if (!el) return
    if (isEditing) {
      el.focus()
      const selection = window.getSelection()
      const range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(false)
      selection?.removeAllRanges()
      selection?.addRange(range)
      return
    }
    // Clear selection on exit, else the highlighted range stays painted.
    el.blur()
    const selection = window.getSelection()
    if (selection && el.contains(selection.anchorNode)) {
      selection.removeAllRanges()
    }
  }, [isEditing])

  // Teardown for the active drag's window listeners; run on unmount so a box
  // removed mid-drag (delete / undo / globe switch) doesn't leak them.
  const dragCleanup = React.useRef<(() => void) | null>(null)
  React.useEffect(() => () => dragCleanup.current?.(), [])

  const startMove = (e: React.PointerEvent): void => {
    // Require a prior selection to move (matches shapes — a body is only draggable
    // once selected): on an unselected box startMove no-ops, so the click selects
    // it instead of dragging it.
    if (isEditing || !isSelected || !wrapperRef.current) return
    e.stopPropagation()
    // Capture so the drag owns the pointer: the release never lands on the bare
    // map (which would deselect) and only this pointer drives the move (a second
    // finger is ignored). pointercancel ends it like pointerup so an interrupted
    // touch can't strand the gesture.
    const pointerId = e.pointerId
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(pointerId)
    const canvas = map.getCanvas().getBoundingClientRect()
    // Grab offset from the box's centre (screen.x/y is the centre anchor), not the AABB.
    const grabX = e.clientX - canvas.left - screen.x
    const grabY = e.clientY - canvas.top - screen.y
    const onPointerMove = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return
      markMovedIfDragged(ev.clientX, ev.clientY)
      const lngLat = map.unproject([
        ev.clientX - grabX - canvas.left,
        ev.clientY - grabY - canvas.top,
      ])
      onMove(lngLat.lng, lngLat.lat)
    }
    const cleanup = (): void => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      dragCleanup.current = null
    }
    const onPointerUp = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return
      cleanup()
    }
    dragCleanup.current = cleanup
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  const startResize = (e: React.PointerEvent): void => {
    if (!editableRef.current) return
    e.preventDefault()
    e.stopPropagation()
    // Layout width (ignores the rotate transform), not the inflated AABB width.
    const startWidth = editableRef.current.offsetWidth
    const startX = e.clientX
    const startY = e.clientY
    const pointerId = e.pointerId
    // The box's local +x axis projects to screen direction u under the 3D transform
    // rotateX(pitch) rotateZ(-bearing) … rotate(box.rotation): the net in-plane angle is
    // (rotation - bearing), and rotateX foreshortens screen-y by cos(pitch).
    const inPlane = ((box.rotation - bearing) * Math.PI) / 180
    const cosP = Math.cos((pitch * Math.PI) / 180)
    const ux = Math.cos(inPlane)
    const uy = Math.sin(inPlane) * cosP
    // |u|^2 stays > 0 at mapbox's <=85deg pitch cap; floor it so a fully foreshortened,
    // screen-vertical edge can't spike the width sensitivity.
    const uLenSq = Math.max(ux * ux + uy * uy, 1e-3)
    const onPointerMove = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return
      // Least-squares-project the screen drag onto the box's x-axis image, in unscaled local
      // px (offsetWidth is pre-scale). Reduces to (dx*cos + dy*sin)/scale at pitch=bearing=0.
      const localDelta =
        ((ev.clientX - startX) * ux + (ev.clientY - startY) * uy) /
        (scale * uLenSq)
      onResize(startWidth + localDelta)
    }
    const cleanup = (): void => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      dragCleanup.current = null
    }
    const onPointerUp = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return
      cleanup()
    }
    dragCleanup.current = cleanup
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  // transform-origin is the rotation pivot, so the AABB centre is stable — measure the angle against it.
  const startRotate = (e: React.PointerEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    if (!wrapperRef.current) return // Keep the release off the map canvas (a bare-map pointerup deselects).
    const pointerId = e.pointerId
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(pointerId)
    const rect = wrapperRef.current.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    // rotateX(pitch) foreshortens screen-y by cos(pitch); un-foreshorten the y-offset so the
    // angle is measured in the box's own tilted plane. Its delta equals the box.rotation delta
    // (the constant -bearing and the handle's fixed offset cancel in angle - startAngle).
    const cosP = Math.max(Math.cos((pitch * Math.PI) / 180), 1e-3)
    const startAngle = Math.atan2((e.clientY - cy) / cosP, e.clientX - cx)
    const startRotation = box.rotation
    const onPointerMove = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return
      const angle = Math.atan2((ev.clientY - cy) / cosP, ev.clientX - cx)
      let deg = startRotation + ((angle - startAngle) * 180) / Math.PI
      if (ev.shiftKey) deg = Math.round(deg / 15) * 15
      onRotate(deg)
    }
    const cleanup = (): void => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      dragCleanup.current = null
    }
    const onPointerUp = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return
      cleanup()
    }
    dragCleanup.current = cleanup
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  const textStyle: React.CSSProperties = {
    ...textMarkupBoxStyle(specOfBox(box)),
    outline: 'none',
    minWidth: 8,
    minHeight: box.fontSize,
    cursor: isEditing ? 'text' : 'move',
  }

  // Unselected/selected, the box is a 3D ground object: it lies on the map plane
  // (rotateX(pitch)), turns with the map (rotateZ(-bearing)) and carries the user's own
  // rotation. While editing it drops ALL of that and faces the viewport upright (scale
  // only) — so the text is horizontal, readable, and the caret works — then lies back down
  // to its 3D pose on exit. Both are identical at the default north-up/top-down pose with
  // no box rotation.
  const wrapperTransform = isEditing
    ? `translate(-50%, -50%) scale(${scale})`
    : `translate(-50%, -50%) rotateX(${pitch}deg) rotateZ(${-bearing}deg) scale(${scale})${
        box.rotation ? ` rotate(${box.rotation}deg)` : ''
      }`

  // The wrapper scales with zoom to size the text; counter-scale the selection chrome
  // (stroke + handles + rotate pin) by 1/scale so it stays a constant screen size, like
  // the screen-constant handles of the other markup types. At scale 1 these are no-ops.
  const invScale = 1 / scale
  // Centre each corner handle on its corner, then cancel the wrapper scale so it's fixed-size.
  const handleScale = `translate(-50%, -50%) scale(${invScale})`

  return (
    <div
      ref={wrapperRef}
      data-markup-text-box={box.id}
      className={`tw:absolute ${
        interactive || isEditing
          ? 'tw:pointer-events-auto'
          : 'tw:pointer-events-none'
      } ${isSelected ? 'tw:outline-solid tw:outline-primary' : ''}`}
      style={{
        left: screen.x,
        top: screen.y,
        // Padding scales with the box, as in the saved view; the selection stroke sits
        // outside it, counter-scaled to stay ~2px, so selecting never moves the text.
        padding: TEXT_PADDING_PX,
        outlineWidth: isSelected ? 2 * invScale : undefined,
        backgroundColor: box.fillColor !== NO_FILL ? box.fillColor : undefined,
        transform: wrapperTransform,
        transformOrigin: 'center',
        // none unless editing: the box is pointer-events:auto so it catches the touch —
        // touch-action:auto would leak a drag to the page as scroll / pull-to-refresh.
        touchAction: isEditing ? undefined : 'none',
      }}
      onPointerDown={e => {
        movedRef.current = false
        downRef.current = { x: e.clientX, y: e.clientY }
        startMove(e)
      }}
      onPointerUp={e => {
        const down = downRef.current
        downRef.current = null
        // A drag (move/resize/pan) is never a tap, and mustn't pair into a double-tap.
        if (!down || movedRef.current) {
          onTapCancel()
          return
        }
        if (isEditing) return // taps inside an editing box drive the caret
        onTap(e.clientX, e.clientY, e.timeStamp)
      }}
      onPointerCancel={() => {
        downRef.current = null
      }}
    >
      <div
        ref={editableRef}
        data-markup-text-editable={box.id}
        contentEditable={isEditing}
        suppressContentEditableWarning={true}
        style={textStyle}
        onInput={e =>
          onTextChange((e.currentTarget as HTMLDivElement).innerText)
        }
        onBlur={onBlur}
      />
      {isSelected && !isEditing && (
        <>
          <span
            className={HANDLE}
            style={{ left: 0, top: 0, transform: handleScale }}
          />
          <span
            className={HANDLE}
            style={{ left: '100%', top: 0, transform: handleScale }}
          />
          <span
            className={HANDLE}
            style={{ left: 0, top: '100%', transform: handleScale }}
          />
          <span
            className={HANDLE}
            style={{
              left: '100%',
              top: '100%',
              transform: handleScale,
              cursor: 'nwse-resize',
            }}
            onPointerDown={startResize}
          />
          {/* Rotate pin hangs off the box bottom-centre by a stem (rotates with the
            wrapper, so the chrome stays attached at any angle). Wrapped in a
            counter-scaled container so the stem + pin keep a constant screen size and
            offset regardless of the box's zoom scale. */}
          <div
            className="tw:absolute"
            style={{
              left: '50%',
              top: '100%',
              transform: `translateX(-50%) scale(${invScale})`,
              transformOrigin: 'top center',
            }}
          >
            <span
              className="tw:absolute tw:w-[1.5px] tw:-translate-x-1/2 tw:bg-primary"
              style={{ left: '50%', top: 0, height: 12 }}
            />
            <span
              className={`tw:absolute tw:-translate-x-1/2 ${ROTATE_HANDLE_CLASS}`}
              style={{ left: '50%', top: 12, cursor: 'grab' }}
              onPointerDown={startRotate}
            >
              <RotateCwIcon className="tw:size-2.5 tw:text-primary" />
            </span>
          </div>
        </>
      )}
    </div>
  )
}

export default observer(MarkupTextBox)
